import { codeFromText } from "@libs/identity-ui/paste";
import { useEffect, useState } from "react";
import type { RefObject } from "react";
import { Platform, StyleSheet, Text, TextInput, View } from "react-native";
import type { LayoutChangeEvent, StyleProp, ViewStyle } from "react-native";
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import type { AnimatedStyle } from "react-native-reanimated";

/** How the slots are drawn: the sheet they sit in sets it. */
export type CodeSlotsLook = {
  text: string;
  surface: string;
  border: string;
  /** The outline of a slot holding a digit. */
  filledBorder: string;
  /** The outline of the slot the caret is in. */
  focus: string;
  danger: string;
  success: string;
  /** The fill of the slots once the code is accepted. */
  successFill: string;
  slotHeight: number;
  slotRadius: number;
  slotGap: number;
  digitSize: number;
  digitFont?: string;
};

/**
 * The one-time code: six slots over one real input, as Link in Bio's
 * (`CodeSlots` in @libs/identity-ui). The slots are drawn; the input over
 * them is invisible and does all the work, so a code autofills or pastes in
 * one go. A paste is read for its code, so "663 970" or the sentence around
 * a code fills the slots. Verification and the words around it are the
 * host's.
 */
export function CodeSlots({
  value,
  onChange,
  inputRef,
  look,
  busy = false,
  dimWhenBusy = true,
  invalid = false,
  accepted = false,
  autoFocus = false,
  length = 6,
  label = "Six-digit code",
  testID,
  drawnStyle,
  onLayout,
}: {
  value: string;
  onChange: (value: string) => void;
  inputRef?: RefObject<TextInput | null>;
  look: CodeSlotsLook;
  /** Verifying: the slots take no input. The input keeps its focus, and the
   *  phone its keyboard, so a rejected code is retyped at once. */
  busy?: boolean;
  /** Off for a host that shows the wait itself: busy slots keep their
   *  contrast. */
  dimWhenBusy?: boolean;
  /** The last code was wrong: the slots turn the danger colour. */
  invalid?: boolean;
  /** The code was accepted: the slots turn the success colour. */
  accepted?: boolean;
  autoFocus?: boolean;
  length?: number;
  label?: string;
  testID?: string;
  /** For the drawn slots only, never the input: a host that draws their
   *  arrival hides them without taking the keyboard's field away. */
  drawnStyle?: StyleProp<AnimatedStyle<ViewStyle>>;
  onLayout?: (event: LayoutChangeEvent) => void;
}) {
  const [focused, setFocused] = useState(autoFocus);
  const active = focused && !busy ? Math.min(value.length, length - 1) : -1;

  return (
    <View onLayout={onLayout}>
      <Animated.View
        aria-hidden
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[{ flexDirection: "row", gap: look.slotGap }, drawnStyle]}
      >
        {Array.from({ length }, (_, index) => {
          const digit = value[index];
          const isActive = index === active;
          return (
            <View
              key={index}
              style={[
                styles.slot,
                {
                  backgroundColor: accepted ? look.successFill : look.surface,
                  borderColor: invalid
                    ? look.danger
                    : accepted
                      ? look.success
                      : isActive
                        ? look.focus
                        : digit
                          ? look.filledBorder
                          : look.border,
                  borderRadius: look.slotRadius,
                  height: look.slotHeight,
                  opacity: busy && dimWhenBusy && !accepted ? 0.6 : 1,
                },
              ]}
            >
              {digit ? (
                <Text
                  style={{
                    color: look.text,
                    fontFamily: look.digitFont,
                    fontSize: look.digitSize,
                    fontVariant: ["tabular-nums"],
                    fontWeight: look.digitFont ? undefined : "600",
                  }}
                >
                  {digit}
                </Text>
              ) : isActive ? (
                <Caret color={look.text} height={look.digitSize * 1.1} />
              ) : null}
            </View>
          );
        })}
      </Animated.View>
      <TextInput
        ref={inputRef}
        accessibilityLabel={label}
        autoComplete={Platform.OS === "android" ? "sms-otp" : "one-time-code"}
        autoFocus={autoFocus}
        caretHidden
        inputMode="numeric"
        keyboardType="number-pad"
        onBlur={() => setFocused(false)}
        onChangeText={(text) => {
          // No `maxLength`: it would cut a pasted "663 970" to "663 97"
          // before this reads the code out of it.
          const next = busy
            ? value
            : /^\d*$/.test(text) && text.length <= length
              ? text
              : codeFromText(text, length);
          // What was typed and not taken (a seventh digit, a letter, anything
          // while a code is checked) leaves the field as well.
          if (next !== text) inputRef?.current?.setNativeProps({ text: next });
          if (next !== value) onChange(next);
        }}
        onFocus={() => setFocused(true)}
        style={[StyleSheet.absoluteFill, styles.input]}
        testID={testID}
        textContentType="oneTimeCode"
        value={value}
      />
    </View>
  );
}

function Caret({ color, height }: { color: string; height: number }) {
  const reducedMotion = useReducedMotion();
  const shown = useSharedValue(1);
  useEffect(() => {
    // Reduced motion ends every timing at once, so a blink would rest at
    // its last step, unseen: the caret holds still instead.
    if (reducedMotion) {
      shown.set(1);
      return;
    }
    shown.set(
      withRepeat(
        withSequence(
          withTiming(1, { duration: 500 }),
          withTiming(0, { duration: 500 }),
        ),
        -1,
      ),
    );
  }, [reducedMotion, shown]);
  const style = useAnimatedStyle(() => ({ opacity: shown.get() }));
  return (
    <Animated.View
      style={[{ backgroundColor: color, height, width: 1.5 }, style]}
    />
  );
}

const styles = StyleSheet.create({
  slot: {
    alignItems: "center",
    borderWidth: 1,
    flex: 1,
    justifyContent: "center",
  },
  // Invisible, never absent: iOS gives no keyboard to a field it cannot see.
  input: {
    color: "transparent",
    fontSize: 16,
    opacity: Platform.OS === "ios" ? 0.02 : 0,
  },
});
